import valueParser from 'postcss-value-parser';
import {logger, walk} from './util.js';
import type {Fragment} from './fragments';
import type {GCPMContext, TargetCounter} from "./gcpm";
import { type Pseudo} from "./gcpm.js";
import {breakedAfterSym, type BreakedElement} from "./breaks.js";
import type Mutations from "./mutations.js";
import type {Page} from "./pages";

export type CountersValue = {
	[counterName: string]: number
};

export function parseCounterProp(value: string): CountersValue {
	const counters: CountersValue = {};
	if (value == 'none') return counters;
	const vals = valueParser(value);

	const words = vals.nodes.filter((node) => node.type == 'word');
	for (let i = 0; i < words.length; i++) {
		const name = words[i].value;
		const value = parseInt(words[i + 1].value);
		i++;
		counters[name] = value;
	}
	return counters;
}

export function getCounterProp(value: CountersValue): string {
	return Object.entries(value).flat().join(' ');
}


export type CountersPseudo = "before" | "after";

export const counterScopesSym = Symbol("ps-counter-scopes");
export const counterScopesCarrierSym = Symbol("ps-counter-scopes-carrier");
export const counterChangeScopesSym = Symbol("ps-counter-change-scope");

declare global {
	interface Element {
		[counterScopesSym]?: CounterScopes;
		[counterScopesCarrierSym]?: BreakedElement;
		[counterChangeScopesSym]?: CounterScopes;
	}
}

export type CounterScopes = Record<string, CounterScope>;

export interface CounterScope {
	cursor?: number;
	changes: CounterChange[];
}

export interface CounterChange {
	element: Element;
	pseudo?: Pseudo;
	reset?: boolean;
	value: number;
}

type CountersValueMap = Map<Element, CountersValue>;

type CountersToWrite = {
	onElem: CountersValueMap;
	onDesc: CountersValueMap;
}

export function parseCounters(root: Element, from?: Element, scopesElems: Record<string, Element[]> = {}, gcpmContext?: GCPMContext): void {
	let entered = false;
	const walker = walk.createWalker(document.documentElement, walk.isDisplayedElement);
	walker.currentNode = from || root;

	const targetCountersVals = new Map<TargetCounter[], number>();
	const stop = {};
	try {
		while (!entered || root.contains(walker.currentNode)) {
			if (!entered) entered = walker.currentNode == root;
			addElementCounters(walker.currentNode, scopesElems, null);
			addElementCounters(walker.currentNode, scopesElems, "marker");
			addElementCounters(walker.currentNode, scopesElems, "before");
			if (gcpmContext?.targetCounters) {
				const elemTargetCounters = gcpmContext.targetCounters[walker.currentNode.id];
				if (elemTargetCounters) {
					for (const counterName in elemTargetCounters) {
						const targetCounters = elemTargetCounters[counterName];
						const scopeElem = scopesElems[counterName]?.at(-1);
						if (scopeElem) {
							const scope = scopeElem[counterScopesSym][counterName];
							targetCountersVals.set(targetCounters, getScopeCounterValue(scope, walker.currentNode));
							delete elemTargetCounters[counterName];
						}
					}
					if (!Object.keys(elemTargetCounters).length) delete gcpmContext.targetCounters[walker.currentNode.id];
				}
			}
			if (!walker.firstChild()) {
				// Empty root
				if (walker.currentNode === root) throw stop;
				while (!walker.nextSibling()) {
					addElementCounters(walker.currentNode, scopesElems, "after");
					walker.parentNode();
					for (const counterName in scopesElems) {
						const scopeElems = scopesElems[counterName];
						if (scopeElems && scopeElems.length) {
							const lastScopeElem = scopeElems.at(-1);
							if (lastScopeElem == walker.currentNode) scopeElems.pop();
						}
					}
					if (walker.currentNode === root) throw stop;
				}
			}
		}
	} catch (e) {
		if (e !== stop) throw e;
	}

	for (const [ targetCounters, value ] of targetCountersVals) {
		for (const targetCounter of targetCounters) {
			const elem = targetCounter.element;
			if (isNaN(value)) {
				elem.style.setProperty(targetCounter.varName, `""`);
			} else {
				const counterSets = parseCounterProp(getComputedStyle(elem).counterSet);
				counterSets[targetCounter.varName] = value;
				elem.style.counterSet = getCounterProp(counterSets);
				elem.style.setProperty(targetCounter.varName, `counter(${targetCounter.varName}, ${targetCounter.style || 'decimal'})`);
			}
		}
	}
}

export function addElementCounters(element: Element, scopesElems: Record<string, Element[]>, pseudo: Pseudo, style?: CSSStyleDeclaration): void {
	if (!style) style = getComputedStyle(element, pseudo ? '::' + pseudo : null);
	if (pseudo && style.content == 'none') return;

	const resets = parseCounterProp(style.counterReset);
	for (const counterName in resets) {
		ensureScope(element, pseudo, counterName, scopesElems, true).changes.push({
			element,
			pseudo,
			reset: true,
			value: resets[counterName],
		});
	}

	const sets = parseCounterProp(style.counterSet);
	if (Object.keys(sets).length) {
		if (!pseudo) (element as HTMLElement).style.counterSet = getCounterProp(sets);
		for (const counterName in sets) {
			ensureScope(element, pseudo, counterName, scopesElems).changes.push({
				element,
				pseudo,
				value: sets[counterName],
			});
		}
	}

	const increments = parseCounterProp(style.counterIncrement);
	for (const counterName in increments) {
		if (!(counterName in sets)) {
			const scope = ensureScope(element, pseudo, counterName, scopesElems);
			const lastChangeValue = scope.changes.at(-1)?.value || 0;
			scope.changes.push({
				element,
				pseudo,
				value: lastChangeValue + increments[counterName]
			});
		}
	}
}

export function getCounterScopes(elem: Element): CounterScopes {
	let scopes = elem[counterScopesSym];
	if (!scopes && counterScopesCarrierSym in elem) scopes = elem[counterScopesCarrierSym][counterScopesSym];
	return scopes;
}

export function reconstructScopeElems(start: Element): Record<string, Element[]> {
	let elem = start;
	const scopesElems: Record<string, Element[]> = {};
	while (elem) {
		const scopes = getCounterScopes(elem);
		if (scopes) {
			for (const counterName in scopes) {
				if (!(counterName in scopesElems)) scopesElems[counterName] = [ elem ];
			}
		}
		elem = elem.parentElement;
	}
	return scopesElems;
}

function ensureScope(element: Element, pseudo: string, counterName: string, scopesElems: Record<string, Element[]>, sameRoot?: boolean): CounterScope {
	const scopeRoot = pseudo ? element : element.parentElement || element;
	let scopeElems = scopesElems[counterName];
	let scope: CounterScope;
	if (scopeElems && scopeElems.length && (!sameRoot || scopeElems.at(-1) == scopeRoot)) {
		scope = scopeElems.at(-1)[counterScopesSym][counterName];
	} else {
		scope = {
			changes: []
		};
		if (!scopeElems) scopeElems = scopesElems[counterName] = [];
		scopeElems.push(scopeRoot);
		let elemScopes = scopeRoot[counterScopesSym];
		if (!elemScopes) elemScopes = scopeRoot[counterScopesSym] = {};
		elemScopes[counterName] = scope;
	}
	if (!(counterChangeScopesSym in element)) element[counterChangeScopesSym] = {};
	element[counterChangeScopesSym][counterName] = scope;
	return scope;
}

export function writePageCounters(page: Page, mutations: Mutations): void {
	const dest = page.container.parentElement;
	const scopes = dest[counterScopesSym];
	if (!scopes) return;

	const toWrite = { onElem: new Map(), onDesc: new Map() };

	toWrite.onElem.set(page.container, {});

	for (const counterName in scopes) {
		const scope = scopes[counterName];
		const startChange = scope.changes[0];
		if (startChange.element.localName == 'ps-page') {
			// Recherche du dernier changement dont l'élément est déjà paginé
			let value = 0;
			let isCurrentPage = false;
			for (const change of scope.changes) {
				value = change.value;

				// Arrêt du parcours dès qu'on a dépassé la page courante
				if (change.element == page.container) isCurrentPage = true;
				else if (isCurrentPage) break;
			}
			toWrite.onElem.get(page.container)[counterName] = value;
		}
	}
	writeElemCounters(dest, mutations, toWrite);
	writeBreakCounters(page.body, mutations, toWrite);

	writeCounters(toWrite, mutations);
}

export function writeBreakCounters(root: BreakedElement, mutations: Mutations, countersToWrite?: CountersToWrite): void {
	let elem = root;

	const toWrite = countersToWrite || { onElem: new Map(), onDesc: new Map() };

	while (elem && (elem == root || walk.isBreakedBefore(elem) || elem.localName.startsWith('ps-'))) {
		writeElemCounters(elem, mutations, toWrite);

		elem = elem.firstElementChild as BreakedElement;
		while (elem && elem.hasAttribute('ps-repeated')) elem = elem.nextElementSibling as BreakedElement;
	}

	if (!countersToWrite) writeCounters(toWrite, mutations);
}

export function writeElemCounters(elem: HTMLElement, mutations: Mutations, countersToWrite?: CountersToWrite): void {
	const scopeRoot = elem.localName == 'ps-column-body' ? elem.closest('[ps-columns]') : elem;
	const scopes = getCounterScopes(scopeRoot);
	if (!scopes) return;

	const toWrite = countersToWrite || { onElem: new Map(), onDesc: new Map() };

	for (const counterName in scopes) {
		const scope = scopes[counterName];
		let closestResetIndex = scope.cursor || 0;
		let closestReset = scope.changes[closestResetIndex];
		if (closestReset.element.localName != 'ps-page') {
			let value = 0;

			// Searching for the last change whose element is already paginated
			let i = closestResetIndex;
			let lastChange: CounterChange;
			while (i < scope.changes.length && isProcessedOrPreceding(elem, scope.changes[i].element)) {
				lastChange = scope.changes[i];
				if (lastChange.reset) {
					closestResetIndex = i;
					closestReset = lastChange;
				}
				value = lastChange.value;
				i++;
			}

			let closestBreaked: HTMLElement;
			if (lastChange) {
				closestBreaked = searchCurrentScopeElem(closestReset.element, elem) as BreakedElement;
				mutations.setProp(scope, 'cursor', closestResetIndex);

				if (closestBreaked && walk.isBreakedBefore(closestBreaked)) {
					if (!toWrite.onElem.has(closestBreaked)) toWrite.onElem.set(closestBreaked, parseCounterProp(closestBreaked.style.counterSet));
					toWrite.onElem.get(closestBreaked)[counterName] = value;
				} else if (walk.isBreakedBefore(elem)) {
					if (!toWrite.onDesc.has(elem)) toWrite.onDesc.set(elem, parseCounterProp(elem.style.getPropertyValue('--ps-counters-on-desc')));
					toWrite.onDesc.get(elem)[counterName] = value;
				}
			}
		}
	}

	if (!countersToWrite) writeCounters(toWrite, mutations);
}

export function writeCounters(toWrite: CountersToWrite, mutations: Mutations): void {
	for (const [elem, counterSet] of toWrite.onElem) {
		mutations.setAttr(elem, 'ps-counters', '');
		mutations.setStyle(elem as HTMLElement, {'counter-set': getCounterProp(counterSet)});
	}

	for (const [elem, counterSetOnDesc] of toWrite.onDesc) {
		mutations.setAttr(elem, 'ps-counters-on-desc', '');
		mutations.setStyle(elem as HTMLElement, {'--ps-counters-on-desc': getCounterProp(counterSetOnDesc)});
	}
}

/** Search the first breaked following sibling of a scope element, contained by currentRoot */
function searchCurrentScopeElem(elem: Element, currentRoot: Element): Element {
	while (elem && (walk.inFragment(elem) || !currentRoot.contains(elem))) elem = (elem as BreakedElement)[breakedAfterSym];
	if (!elem?.isConnected) return null;
	return elem;
}

function isProcessedOrPreceding(refNode: Element, testNode: Element): boolean {
	return walk.inFragment(testNode) || (refNode.getRootNode().contains(testNode) && walk.isPreceding(refNode, testNode));
}

export function getScopeCounterValue(scope: CounterScope, elem: Element): number {
	let lastChange;
	for (const change of scope.changes) {
		if (!walk.inFragment(change.element) && elem != change.element && !walk.isPreceding(elem, change.element)) break;
		lastChange = change;
	}
	return lastChange ? lastChange.value : NaN;
}

// TODO Possible problem if a counter-reset or counter-increment exists on the documentElement
export function getCounterValueFromStyle(element: Element, counterName: string): number {
	const doc = element.ownerDocument;
	const walker = walk.createWalker(doc.documentElement, walk.isElement);
	let counterValue = 0;
	walker.currentNode = element;
	while (walker.currentNode != doc.documentElement) {
		let counterSetted = false;
		const style = getComputedStyle(walker.currentNode);
		if (style.display != 'none') {

			const sets = parseCounterProp(style.counterSet);
			if (counterName in sets) {
				counterValue += sets[counterName];
				counterSetted = true;
			} else {
				const resets = parseCounterProp(style.counterReset);
				if (counterName in resets) {
					counterValue += resets[counterName];
					counterSetted = true;
				}

				const increments = parseCounterProp(style.counterIncrement);
				if (counterName in increments) counterValue += increments[counterName];
			}

			if (counterSetted) return counterValue;
		}
		walker.previousNode();

	}
	logger.warn(`Counter '${counterName}' without a counter-reset.`);
	return counterValue;
}

export function getCounterValueFromScopes(elem: Element, counterName: string): number {
	let current = elem;
	while (elem) {
		if (counterChangeScopesSym in elem) {
			if (counterName in elem[counterChangeScopesSym]) {
				const scope = elem[counterChangeScopesSym][counterName];
				return getScopeCounterValue(scope, elem);
			}
		}
		const scopes = getCounterScopes(current);
		if (scopes) {
			if (counterName in scopes) {
				const scope = scopes[counterName];
				return getScopeCounterValue(scope, elem);
			}
		}
		current = current.parentElement;
	}
	return 0;
}

export function getPageRawCounterValue(pages: Fragment[], element: Element): number {
	let pageBox = element;
	while (pageBox && pageBox.localName != 'ps-page') pageBox = pageBox.parentElement;

	for (let i = 0; i < pages.length; i++) {
		if (pageBox == pages[i].container) return i + 1;
	}

	throw "Page not found";
}
